import { ConfigApi, createApiRef } from '@backstage/core-plugin-api';
import { readGithubIntegrationConfigs } from '@backstage/integration';
import { ScmAuthApi } from '@backstage/integration-react';
import { Octokit } from '@octokit/rest';

export interface AppExtensionPreviewImageApi {
  getPreviewImageDataUrl(options: {
    sourceRepository: string;
    previewImageUrl: string;
  }): Promise<string | undefined>;
}

export const appExtensionPreviewImageApiRef =
  createApiRef<AppExtensionPreviewImageApi>({
    id: 'plugin.app-extensions.preview-image',
  });

const isAbsoluteUrl = (value: string) => /^(https?:|data:)/.test(value);

const CONTENT_TYPES_BY_EXTENSION: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.svg': 'image/svg+xml',
  '.webp': 'image/webp',
};

function inferContentType(path: string): string {
  const dot = path.lastIndexOf('.');
  const extension = dot === -1 ? '' : path.slice(dot).toLowerCase();
  return CONTENT_TYPES_BY_EXTENSION[extension] ?? 'application/octet-stream';
}

/**
 * Fetches an extension's preview image directly from GitHub using the
 * signed-in user's own token — modeled on emisx-releases' GithubClient. This
 * is an authenticated Octokit call (Authorization header via CORS), not a
 * hotlinked <img src> — GitHub blocks the latter for private repos, but
 * api.github.com supports CORS for requests like this one.
 */
export class GithubPreviewImageClient implements AppExtensionPreviewImageApi {
  constructor(
    private readonly options: {
      configApi: ConfigApi;
      scmAuthApi: ScmAuthApi;
    },
  ) {}

  private async getOctokit(hostname: string = 'github.com'): Promise<Octokit> {
    const { token } = await this.options.scmAuthApi.getCredentials({
      url: `https://${hostname}/`,
      additionalScope: {
        customScopes: {
          github: ['repo'],
        },
      },
    });
    const configs = readGithubIntegrationConfigs(
      this.options.configApi.getOptionalConfigArray('integrations.github') ??
        [],
    );
    const integrationConfig = configs.find(v => v.host === hostname);
    return new Octokit({ auth: token, baseUrl: integrationConfig?.apiBaseUrl });
  }

  async getPreviewImageDataUrl(options: {
    sourceRepository: string;
    previewImageUrl: string;
  }): Promise<string | undefined> {
    const { sourceRepository, previewImageUrl } = options;
    if (isAbsoluteUrl(previewImageUrl)) {
      return previewImageUrl;
    }

    const [owner, repo] = sourceRepository.split('/');
    if (!owner || !repo) {
      return undefined;
    }

    try {
      const octokit = await this.getOctokit();
      const { data } = await octokit.repos.getContent({
        owner,
        repo,
        path: previewImageUrl,
      });

      if (Array.isArray(data) || data.type !== 'file' || !data.content) {
        return undefined;
      }

      return `data:${inferContentType(previewImageUrl)};base64,${data.content}`;
    } catch {
      return undefined;
    }
  }
}
